/**
 * Navigation model — structure preserved from IMPLEMENTATION_MASTER_PLAN.md
 * §7.3 (three groups: Principal / Opérations / Configuration), by explicit
 * supervisor decision (2026-08-31): the visual identity is new, the
 * information architecture is not.
 *
 * Every item here is a real, working screen — the last four ("Clients",
 * "Marketing", "Paiements & COD", "Paramètres") joined at the "Élimination
 * BIENTÔT" lot (docs/resumée.txt), closing the platform's explicit rule
 * for this app: no nav item may point at an unbuilt screen. Each of the
 * four is grounded in real data (client/payment views are aggregated from
 * `GET /merchant/orders`, no new backend endpoint — see
 * `features/clients/api.ts` and `features/payments/api.ts`'s own headers);
 * where a sub-feature genuinely has no backend yet (campaigns, notification
 * preferences), the SCREEN still renders — it says so honestly in place
 * of the feature, never a blank route or a "Bientôt" badge in this sidebar.
 *
 * `NavItem` therefore carries no `status` field any more: there is no
 * "not yet built" state left to represent. Earlier entries, for reference:
 * Dashboard/Produits & stock/Inventaire (Produits & Stock lot); "Boutique"
 * (entrée 60, `GET`/`PUT /merchant/store`; moved into the Principal group,
 * right after Dashboard, at entrée 108 — onboarding order, not a group
 * reassignment: it stays the same screen and permission, only its position
 * changed); "Équipe & permissions" (entrée
 * 61, `/merchant/staff`, OQ-14); "Commandes" (entrée 70, `/merchant/orders`,
 * Sprint 12); "Livraison" (entrée 71, `/merchant/deliveries/pending`,
 * Sprint 14); "Analytics" (Analytics S17 lot, `GET /merchant/analytics`);
 * "Promotions" (lot 2 Promotions/Coupons, `/merchant/coupons`, OQ-25);
 * "Factures" (lot Facturation S15, `/merchant/invoices`); "Catégories"
 * (lot Catégories, `/merchant/categories`, backend Sprint 7/OQ-19);
 * "À traiter" (remplace l'ancienne page « Notifications », qui n'était que le
 * journal d'e-mails jamais délivrés : aucun fournisseur d'e-mail, OQ-12 —
 * l'écran dérive ses alertes des commandes, du stock, de la livraison et du
 * compte Jibex, sans rien stocker).
 *
 * Ordre validé (2026-10-07), du plus fréquent au plus rare : Principal = Tableau de bord, Boutique, À traiter,
 * Commandes, Produits & stock, Inventaire, Clients, Marketing, Analytics ; Opérations = Livraison, Paiements & COD,
 * Factures ; Configuration = Catégories, Promotions, puis les trois entrées de réglage qui existaient déjà
 * (Compte Jibex, Équipe & permissions, Paramètres), laissées en fin de groupe.
 */
import { type MessageKey } from '@ecom/i18n';

export interface NavItem {
  readonly labelKey: MessageKey;
  readonly to: string;
}

export interface NavGroup {
  readonly titleKey: MessageKey;
  readonly items: readonly NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    titleKey: 'merchant.nav.group.principal',
    items: [
      { labelKey: 'merchant.nav.dashboard', to: '/dashboard' },
      { labelKey: 'merchant.nav.store', to: '/store' },
      { labelKey: 'merchant.nav.todo', to: '/todo' },
      { labelKey: 'merchant.nav.orders', to: '/orders' },
      { labelKey: 'merchant.nav.sales', to: '/sales' },
      { labelKey: 'merchant.nav.products', to: '/products' },
      { labelKey: 'merchant.nav.inventory', to: '/inventory' },
      { labelKey: 'merchant.nav.customers', to: '/customers' },
      { labelKey: 'merchant.nav.marketing', to: '/marketing' },
      { labelKey: 'merchant.nav.analytics', to: '/analytics' },
    ],
  },
  {
    titleKey: 'merchant.nav.group.operations',
    items: [
      { labelKey: 'merchant.nav.delivery', to: '/delivery' },
      { labelKey: 'merchant.nav.payments', to: '/payments' },
      { labelKey: 'merchant.nav.invoices', to: '/invoices' },
    ],
  },
  {
    titleKey: 'merchant.nav.group.configuration',
    items: [
      { labelKey: 'merchant.nav.categories', to: '/categories' },
      { labelKey: 'merchant.nav.promotions', to: '/promotions' },
      { labelKey: 'merchant.nav.deliveryAccount', to: '/delivery/account' },
      { labelKey: 'merchant.nav.team', to: '/team' },
      { labelKey: 'merchant.nav.settings', to: '/settings' },
      // Everyone's own security history (lot B3).
      { labelKey: 'account.nav', to: '/account' },
    ],
  },
];
